import { lazy, type ComponentType } from 'react';
import type { View } from '@/app/navigation/navigation-state';
import type { ViewRegistry } from '@/app/routing/view-registry';
import { useNavigation } from '@/app/navigation/use-navigation';

const Pessoa = lazy(() => import('@/features/pessoa/components/Pessoa'));
const Empreendimento = lazy(() => import('@/features/comercial/components/Empreendimento'));
const Departamento = lazy(() => import('@/features/cadastro/components/Departamento'));
const Estado = lazy(() => import('@/features/cadastro/components/Estado'));
const Cidade = lazy(() => import('@/features/cadastro/components/Cidade'));
const Fornecedor = lazy(() => import('@/features/cadastro/components/Fornecedor'));
const Produto = lazy(() => import('@/features/cadastro/components/Produto'));
const AmbienteTipo = lazy(() => import('@/features/cadastro/components/AmbienteTipo'));
const PontoTipo = lazy(() => import('@/features/cadastro/components/PontoTipo'));
const PontoTipoGrupo = lazy(() => import('@/features/cadastro/components/PontoTipoGrupo'));
const ServicoTipo = lazy(() => import('@/features/cadastro/components/ServicoTipo'));
const VistoriaTipo = lazy(() => import('@/features/cadastro/components/VistoriaTipo'));
const ProfissionalTipo = lazy(() =>
  import('@/features/cadastro/components/profissional').then((m) => ({ default: m.ProfissionalTipo })),
);
const AtrasoMotivo = lazy(() =>
  import('@/features/cadastro/components/atraso').then((m) => ({ default: m.AtrasoMotivo })),
);
const FileCategoria = lazy(() => import('@/features/cadastro/components/FileCategoria'));
const TransitionRulesManager = lazy(
  () => import('@/features/admin/components/config/TransitionRulesManager'),
);
const ObraEtapa = lazy(() => import('@/features/obra/components/ObraEtapa'));
const ObraProdutoStatus = lazy(() => import('@/features/obra/components/ObraProdutoStatus'));
const Modulo = lazy(() => import('@/features/cadastro/components/Modulo'));
import { ApiBetaGate } from '@/features/configuracao';
const ApiKeysPage = lazy(() =>
  import('@/features/configuracao').then((m) => ({ default: m.ApiKeysPage })),
);
const ApiDocsPage = lazy(() =>
  import('@/features/configuracao').then((m) => ({ default: m.ApiDocsPage })),
);
const WebhooksPage = lazy(() =>
  import('@/features/configuracao').then((m) => ({ default: m.WebhooksPage })),
);
const AdminApiKeysPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminApiKeysPage })),
);
const GenericDetails = lazy(() => import('@/components/ui/GenericDetails'));

type NavigateToPath = (path: string, params?: Record<string, unknown>) => void;

function makeOnBackOnly(Component: ComponentType<{ onBack: () => void }>) {
  return function View() {
    const { actions } = useNavigation();
    return <Component onBack={actions.goBack} />;
  };
}

function withBetaGate(Component: ComponentType<unknown>): ComponentType<unknown> {
  return function BetaGated(props: unknown) {
    return (
      <ApiBetaGate>
        <Component {...(props as object)} />
      </ApiBetaGate>
    );
  };
}

/**
 * Sibling navigateTo pattern: Tipo* components declare
 *   navigateTo?: (path: string, params?: Record<string, unknown>) => void
 * Bridge to actions.navigate; path is cast at the boundary (Router throws
 * on unknown view literals at dispatch time).
 */
function makeSiblingView(
  Component: ComponentType<{ onBack: () => void; navigateTo: NavigateToPath }>,
) {
  return function View() {
    const { actions } = useNavigation();
    return (
      <Component
        onBack={actions.goBack}
        navigateTo={(path) => actions.navigate(path as View)}
      />
    );
  };
}

function ProdutoView() {
  const { actions } = useNavigation();
  return (
    <Produto
      onBack={actions.goBack}
      navigateTo={(path, params) => {
        const idParam =
          params && typeof params === 'object' && 'id' in params && typeof (params as { id: unknown }).id === 'string'
            ? (params as { id: string }).id
            : undefined;
        actions.navigate(path as View, {
          viewParams: idParam ? { initialOpenRecordId: idParam } : null,
        });
      }}
    />
  );
}

function DepartamentoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.departamentoId}
      tableName="departamento"
      title="Departamento"
      primaryField="departamento"
      fields={[
        { key: 'departamento', label: 'Nome do Departamento', primary: true },
        { key: 'cod', label: 'Código', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

function EstadoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.estadoId}
      tableName="estado"
      title="Estado"
      primaryField="estado"
      fields={[
        { key: 'estado', label: 'Nome do Estado', primary: true },
        { key: 'uf', label: 'UF', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

function CidadeDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.cidadeId}
      tableName="cidade"
      title="Cidade"
      primaryField="cidade"
      fields={[
        { key: 'cidade', label: 'Nome da Cidade', primary: true },
        { key: 'cod', label: 'Código', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

function TipoAmbienteDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.tipoAmbienteId}
      tableName="ambiente_tipo"
      title="Tipo de Ambiente"
      primaryField="ambiente_tipo"
      fields={[
        { key: 'ambiente_tipo', label: 'Tipo de Ambiente', primary: true },
        { key: 'cod', label: 'Código', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

function TipoPontoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.tipoPontoId}
      tableName="ponto_tipo"
      title="Tipo de Ponto"
      primaryField="ponto_tipo"
      fields={[
        { key: 'ponto_tipo', label: 'Tipo de Ponto', primary: true },
        { key: 'cod', label: 'Código', mono: true },
        { key: 'prefixo', label: 'Prefixo', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

function GrupoTipoPontoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <GenericDetails
      itemId={selection.grupoTipoPontoId}
      tableName="ponto_tipo_grupo"
      title="Grupo de Tipo de Ponto"
      primaryField="ponto_tipo_grupo"
      fields={[
        { key: 'ponto_tipo_grupo', label: 'Grupo', primary: true },
        { key: 'prefixo', label: 'Prefixo', mono: true },
      ]}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={actions.goBack}
    />
  );
}

export const configViews: Partial<ViewRegistry> = {
  pessoas: { component: makeOnBackOnly(Pessoa), guard: { permission: 'PESSOA' } },
  empreendimentos: {
    component: makeOnBackOnly(Empreendimento),
    guard: { permission: 'EMPREENDIMENTO' },
  },
  departamentos: {
    component: makeOnBackOnly(Departamento),
    guard: { permission: 'DEPARTAMENTO' },
  },
  estados: { component: makeOnBackOnly(Estado), guard: { permission: 'ESTADO' } },
  cidades: { component: makeOnBackOnly(Cidade), guard: { permission: 'CIDADE' } },
  fornecedores: { component: makeOnBackOnly(Fornecedor), guard: { permission: 'FORNECEDOR' } },
  produtos: { component: ProdutoView, guard: { permission: 'PRODUTO' } },
  'tipos-ambiente': {
    component: makeOnBackOnly(AmbienteTipo),
    guard: { permission: 'AMBIENTE_TIPO' },
  },
  'tipos-ponto': { component: makeOnBackOnly(PontoTipo), guard: { permission: 'PONTO_TIPO' } },
  'grupos-tipo-ponto': {
    component: makeOnBackOnly(PontoTipoGrupo),
    guard: { permission: 'PONTO_TIPO_GRUPO' },
  },
  'tipos-servico': {
    component: makeSiblingView(ServicoTipo),
    guard: { permission: 'SERVICO_TIPO' },
  },
  'tipos-vistoria': {
    component: makeSiblingView(VistoriaTipo),
    guard: { permission: 'SERVICO_TIPO' },
  },
  'profissional-tipo': {
    component: makeSiblingView(ProfissionalTipo),
    guard: { permission: 'PROFISSIONAL_TIPO' },
  },
  'atraso-motivo': {
    component: makeSiblingView(AtrasoMotivo),
    guard: { permission: 'ATRASO_MOTIVO' },
  },
  'file-categorias': {
    component: makeOnBackOnly(FileCategoria),
    guard: { permission: 'ADMIN_TOOLS' },
  },
  'transition-rules': {
    component: makeOnBackOnly(TransitionRulesManager),
    guard: { permission: 'ADMIN_TOOLS' },
  },
  'obra-etapa': { component: makeOnBackOnly(ObraEtapa), guard: { permission: 'OBRA_ETAPA' } },
  'obra-produto-status': {
    component: makeOnBackOnly(ObraProdutoStatus),
    guard: { screen: 'obra-produto-status' },
  },
  modulos: { component: makeOnBackOnly(Modulo), guard: { permission: 'MODULO' } },
  'departamento-details': {
    component: DepartamentoDetailsView,
    guard: { permission: 'DEPARTAMENTO' },
  },
  'estado-details': { component: EstadoDetailsView, guard: { permission: 'ESTADO' } },
  'cidade-details': { component: CidadeDetailsView, guard: { permission: 'CIDADE' } },
  'tipo-ambiente-details': {
    component: TipoAmbienteDetailsView,
    guard: { permission: 'AMBIENTE_TIPO' },
  },
  'tipo-ponto-details': {
    component: TipoPontoDetailsView,
    guard: { permission: 'PONTO_TIPO' },
  },
  'grupo-tipo-ponto-details': {
    component: GrupoTipoPontoDetailsView,
    guard: { permission: 'PONTO_TIPO_GRUPO' },
  },
  'api-keys': { component: withBetaGate(ApiKeysPage), guard: { permission: 'API_KEY' } },
  'api-keys-admin': { component: withBetaGate(AdminApiKeysPage), guard: { permission: 'API_KEY' } },
  'api-docs': { component: withBetaGate(makeOnBackOnly(ApiDocsPage)), guard: { permission: 'API_KEY' } },
  'webhooks': { component: withBetaGate(WebhooksPage), guard: { permission: 'WEBHOOK' } },
};
